import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faShoppingBag,
  faBug,
  faLink,
  faChevronRight,
  faTruck,
  faUser,
  faMapMarkerAlt,
  faExclamationCircle,
  faMoneyBill,
  faTag,
} from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { PaymentMethodErrorModal } from './errors/PaymentMethodErrorModal';
import { StockErrorModal } from './errors/StockErrorModal';
import {
  GenericErrorModal,
  GENERIC_ERROR_I18N_PREFIX,
  LABEL_RECORD_FAILED_I18N_PREFIX,
} from './errors/GenericErrorModal';
import { UnlinkedProductsErrorModal } from './errors/UnlinkedProductsErrorModal';
import { ShippingDataErrorModal } from './errors/ShippingDataErrorModal';
import { InvalidPersonalDataErrorModal } from './errors/InvalidPersonalDataErrorModal';
import { ZoneOutOfCoverageErrorModal } from './errors/ZoneOutOfCoverageErrorModal';
import { RuleDoesntMatchErrorModal } from './errors/RuleDoesntMatchErrorModal';
import { NoShippingErrorModal } from './errors/NoShippingErrorModal';
import { NoRateForDestinationErrorModal } from './errors/NoRateForDestinationErrorModal';
import type { Order } from '../../models/Order';

export interface SolveErrorsModalRef {
  openModal: () => void;
  closeModal: () => void;
}

interface SolveErrorsModalProps {
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      address1?: string;
      address2?: string;
      zip?: string;
      city?: string;
      province?: string;
      country_code?: string;
    };
  };
  orderData?: Order;
  errors?: import('../../models/Order').OrderError[];
  onRefetch?: () => void;
}

export const SolveErrorsModal = forwardRef<SolveErrorsModalRef, SolveErrorsModalProps>(
  ({ customer, orderData, errors = [], onRefetch }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const modalRef = useRef<ModalCenterRef>(null);

    // Mapeo de error_key a categorías
    const errorKeyToCategory = {
      PAYMENT_METHOD: 'paymentMethod',
      UNLINKED_PAYMENT_METHOD: 'paymentMethod',
      MISSING_STOCK_PRODUCT: 'stock',
      GENERIC_ERROR: 'generic',
      UNLINKED_PRODUCT: 'unlinkedProducts',
      SHIPPING_DATA: 'shippingData',
      WRONG_SHIPPING_DATA: 'shippingData',
      INVALID_PERSONAL_DATA: 'invalidPersonalData',
      ZONE_OUT_OF_COVERAGE: 'zoneOutOfCoverage',
      RULE_DOESNT_MATCH: 'ruleDoesntMatch',
      NO_SHIPPING: 'noShipping',
      NO_RATE_FOR_DESTINATION: 'noRateForDestination',
      LABEL_RECORD_FAILED: 'labelRecordFailed',
    };

    // Determinar qué categorías de errores están presentes
    const presentErrorCategories = new Set(
      errors.map((error) => errorKeyToCategory[error.error_key as keyof typeof errorKeyToCategory]).filter(Boolean),
    );

    // Función para verificar si una categoría debe mostrarse
    const shouldShowCategory = (category: string) => {
      return presentErrorCategories.has(category);
    };

    // Referencias a las modales individuales
    const paymentMethodModalRef = useRef<any>(null);
    const stockModalRef = useRef<any>(null);
    const genericErrorModalRef = useRef<any>(null);
    const unlinkedProductsModalRef = useRef<any>(null);
    const shippingDataModalRef = useRef<any>(null);
    const invalidPersonalDataModalRef = useRef<any>(null);
    const zoneOutOfCoverageModalRef = useRef<any>(null);
    const ruleDoesntMatchModalRef = useRef<any>(null);
    const noShippingModalRef = useRef<any>(null);
    const noRateForDestinationModalRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      openModal: () => modalRef.current?.openModal(),
      closeModal: () => modalRef.current?.closeModal(),
    }));

    // Función para cerrar todas las modales y hacer refetch
    const handleSuccess = () => {
      // Cerrar todas las modales individuales
      paymentMethodModalRef.current?.closeModal();
      stockModalRef.current?.closeModal();
      genericErrorModalRef.current?.closeModal();
      unlinkedProductsModalRef.current?.closeModal();
      shippingDataModalRef.current?.closeModal();
      invalidPersonalDataModalRef.current?.closeModal();
      zoneOutOfCoverageModalRef.current?.closeModal();
      ruleDoesntMatchModalRef.current?.closeModal();
      noShippingModalRef.current?.closeModal();
      noRateForDestinationModalRef.current?.closeModal();

      // Cerrar el modal principal
      modalRef.current?.closeModal();

      // Hacer refetch de los datos
      onRefetch?.();
    };

    const handleCategoryClick = (category: string) => {
      const orderId = orderData?.id;

      if (!orderId) {
        console.error('Order ID not found in SolveErrorsModal');
        return;
      }

      switch (category) {
        case 'paymentMethod':
          paymentMethodModalRef.current?.openModal(orderId);
          break;
        case 'stock':
          if (orderData) {
            stockModalRef.current?.openModal(orderData);
          }
          break;
        case 'generic':
          {
            const genericError = errors.find((e) => e.error_key === 'GENERIC_ERROR');
            genericErrorModalRef.current?.openModal(orderId, genericError?.error_courier_message, {
              translationKeyPrefix: GENERIC_ERROR_I18N_PREFIX,
            });
          }
          break;
        case 'labelRecordFailed':
          {
            const labelError = errors.find((e) => e.error_key === 'LABEL_RECORD_FAILED');
            genericErrorModalRef.current?.openModal(orderId, labelError?.error_courier_message, {
              translationKeyPrefix: LABEL_RECORD_FAILED_I18N_PREFIX,
            });
          }
          break;
        case 'unlinkedProducts':
          if (orderData) {
            unlinkedProductsModalRef.current?.openModal(orderData);
          }
          break;
        case 'shippingData':
          shippingDataModalRef.current?.openModal(orderId);
          break;
        case 'invalidPersonalData':
          invalidPersonalDataModalRef.current?.openModal(orderId);
          break;
        case 'zoneOutOfCoverage':
          if (orderData) {
            zoneOutOfCoverageModalRef.current?.openModal(orderId, orderData);
          }
          break;
        case 'ruleDoesntMatch':
          ruleDoesntMatchModalRef.current?.openModal(orderId);
          break;
        case 'noShipping':
          noShippingModalRef.current?.openModal(orderId);
          break;
        case 'noRateForDestination':
          noRateForDestinationModalRef.current?.openModal(orderId);
          break;
      }
    };

    return (
      <>
        <ModalCenter ref={modalRef} title={t('solveErrors.title')} type="default" width="lg" fullScreenMobile={true}>
          <div className="border border-card-border rounded-lg">
            {/* Método de pago */}
            {shouldShowCategory('paymentMethod') && (
              <button
                onClick={() => handleCategoryClick('paymentMethod')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.paymentMethod')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Producto sin stock */}
            {shouldShowCategory('stock') && (
              <button
                onClick={() => handleCategoryClick('stock')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faShoppingBag} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.outOfStock')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Error genérico */}
            {shouldShowCategory('generic') && (
              <button
                onClick={() => handleCategoryClick('generic')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faBug} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.genericError')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Registro de etiqueta fallido */}
            {shouldShowCategory('labelRecordFailed') && (
              <button
                onClick={() => handleCategoryClick('labelRecordFailed')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faTag} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.labelRecordFailed')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Productos sin enlazar */}
            {shouldShowCategory('unlinkedProducts') && (
              <button
                onClick={() => handleCategoryClick('unlinkedProducts')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.unlinkedProducts')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Datos de envío */}
            {shouldShowCategory('shippingData') && (
              <button
                onClick={() => handleCategoryClick('shippingData')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faTruck} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.shippingData')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Datos personales inválidos */}
            {shouldShowCategory('invalidPersonalData') && (
              <button
                onClick={() => handleCategoryClick('invalidPersonalData')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.invalidPersonalData')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Zona fuera de cobertura */}
            {shouldShowCategory('zoneOutOfCoverage') && (
              <button
                onClick={() => handleCategoryClick('zoneOutOfCoverage')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.zoneOutOfCoverage')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Regla no coincide */}
            {shouldShowCategory('ruleDoesntMatch') && (
              <button
                onClick={() => handleCategoryClick('ruleDoesntMatch')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.ruleDoesntMatch')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {/* Pedido excluido del envío por regla */}
            {shouldShowCategory('noShipping') && (
              <button
                onClick={() => handleCategoryClick('noShipping')}
                className="flex items-center justify-between w-full p-4 border-b border-card-border hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faTruck} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.noShipping')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}

            {shouldShowCategory('noRateForDestination') && (
              <button
                onClick={() => handleCategoryClick('noRateForDestination')}
                className="flex items-center justify-between w-full p-4 hover:bg-hover-press transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full text-white bg-error-buttons light:bg-error flex items-center justify-center">
                    <FontAwesomeIcon icon={faMoneyBill} className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium">{t('solveErrors.noRateForDestination')}</span>
                </div>
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            )}
          </div>
        </ModalCenter>

        {/* Modales individuales */}
        <PaymentMethodErrorModal ref={paymentMethodModalRef} onSuccess={handleSuccess} />
        <StockErrorModal ref={stockModalRef} onSuccess={handleSuccess} />
        <GenericErrorModal ref={genericErrorModalRef} onSuccess={handleSuccess} />
        <UnlinkedProductsErrorModal ref={unlinkedProductsModalRef} onSuccess={handleSuccess} />
        <ShippingDataErrorModal ref={shippingDataModalRef} customer={customer} onSuccess={handleSuccess} />
        <InvalidPersonalDataErrorModal
          ref={invalidPersonalDataModalRef}
          customer={customer}
          onSuccess={handleSuccess}
        />
        <ZoneOutOfCoverageErrorModal ref={zoneOutOfCoverageModalRef} onSuccess={handleSuccess} />
        <RuleDoesntMatchErrorModal ref={ruleDoesntMatchModalRef} onSuccess={handleSuccess} />
        <NoShippingErrorModal ref={noShippingModalRef} onSuccess={handleSuccess} />
        <NoRateForDestinationErrorModal ref={noRateForDestinationModalRef} onSuccess={handleSuccess} />
      </>
    );
  },
);
